import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle2,
  Download,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  HardDrive,
  Loader2,
  Paperclip,
  Plus,
  Upload,
  User
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createClosedDealAttachment,
  downloadClosedDealAttachment,
  getClosedDealAttachmentsByDealId,
  uploadFilesToS3
} from '../services/api/implementation/impl';
import Modal from '../../shared/components/ui/Modal';
import NoDataFoundImg from '../../shared/assets/png/noDataFound.png';

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === '') return null;
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileVisual = (name, mimeType) => {
  const ext = (name?.split('.').pop() || '').toLowerCase();
  const mime = (mimeType || '').toLowerCase();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext) || mime.startsWith('image/')) {
    return { Icon: FileImage, bg: 'bg-violet-50', text: 'text-violet-700', ring: 'ring-violet-100' };
  }
  if (['xls', 'xlsx', 'csv'].includes(ext) || mime.includes('spreadsheet') || mime.includes('excel')) {
    return { Icon: FileSpreadsheet, bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-100' };
  }
  if (['pdf'].includes(ext) || mime === 'application/pdf') {
    return { Icon: FileText, bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-100' };
  }
  if (['doc', 'docx'].includes(ext) || mime.includes('word')) {
    return { Icon: FileText, bg: 'bg-sky-50', text: 'text-sky-700', ring: 'ring-sky-100' };
  }
  return { Icon: File, bg: 'bg-slate-100', text: 'text-slate-600', ring: 'ring-slate-200/80' };
};

const resetUploadForm = (setters) => {
  const { setShowUploadModal, setDocumentName, setSelectedFile, setUploadedFileData } = setters;
  setShowUploadModal(false);
  setDocumentName('');
  setSelectedFile(null);
  setUploadedFileData(null);
};

const DealDocumentAttachments = ({ dealId, onDocumentsChange }) => {
  const queryClient = useQueryClient();
  const modalFileInputRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileData, setUploadedFileData] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const {
    data: listResult,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['closedDealAttachments', dealId],
    queryFn: () => getClosedDealAttachmentsByDealId(dealId),
    enabled: !!dealId
  });

  const attachments = listResult?.attachments ?? [];
  const totalCount = listResult?.meta?.total ?? attachments.length;

  const invalidateAttachments = () => {
    queryClient.invalidateQueries({ queryKey: ['closedDealAttachments', dealId] });
    onDocumentsChange?.();
  };

  const closeModal = () => {
    resetUploadForm({
      setShowUploadModal,
      setDocumentName,
      setSelectedFile,
      setUploadedFileData
    });
    if (modalFileInputRef.current) {
      modalFileInputRef.current.value = '';
    }
  };

  const { mutate: downloadAttachment } = useMutation({
    mutationFn: (attachmentId) => downloadClosedDealAttachment(attachmentId),
    onMutate: (id) => setDownloadingId(id),
    onSuccess: ({ data, filename, contentType }) => {
      const blob = new Blob([data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Download started');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error downloading file');
    },
    onSettled: () => setDownloadingId(null)
  });

  const processFile = async (file) => {
    if (!file) return;

    setSelectedFile(file);
    if (!documentName.trim()) {
      setDocumentName(file.name);
    }

    setIsUploadingFile(true);
    try {
      const uploaded = await uploadFilesToS3([file]);
      if (!uploaded?.length) throw new Error('Upload failed');

      setUploadedFileData({
        name: uploaded[0].name,
        url: uploaded[0].url,
        type: file.type || uploaded[0].type,
        size: file.size
      });
      toast.success('File uploaded');
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to upload file');
      setSelectedFile(null);
      setUploadedFileData(null);
      if (modalFileInputRef.current) modalFileInputRef.current.value = '';
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleModalFileChange = (event) => {
    const file = event.target.files?.[0];
    processFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    processFile(file);
  };

  const handleSubmitAttachment = async (e) => {
    e.preventDefault();
    const trimmedName = documentName.trim();

    if (!trimmedName) {
      toast.error('Document name is required');
      return;
    }
    if (trimmedName.length > 255) {
      toast.error('Document name must be 255 characters or less');
      return;
    }
    if (!uploadedFileData?.url) {
      toast.error('Please upload a file');
      return;
    }
    if (!dealId) return;

    setIsSubmitting(true);
    try {
      await createClosedDealAttachment({
        deal_id: dealId,
        name: trimmedName,
        url: uploadedFileData.url,
        mime_type: uploadedFileData.type || selectedFile?.type || undefined,
        size_bytes: uploadedFileData.size ?? selectedFile?.size
      });
      toast.success('Document attached successfully');
      invalidateAttachments();
      closeModal();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to attach document'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasAttachments = attachments.length > 0;
  const isListLoading = (isLoading || isFetching) && !hasAttachments;
  const showEmpty = !isListLoading && !hasAttachments;

  return (
    <>
      <div className="mt-2 w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
        {/* Header */}
        <div className="border-b border-slate-100/80 bg-gradient-to-r from-teal-50/90 via-white to-slate-50 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 gap-3">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-700/12 text-teal-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
                aria-hidden>
                <Paperclip className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold tracking-tight text-slate-900">
                    Document attachment
                  </h2>
                  {hasAttachments && (
                    <span className="inline-flex items-center rounded-full border border-slate-200/80 bg-white/90 px-2.5 py-0.5 text-xs font-medium text-slate-600 shadow-sm">
                      {totalCount} file{totalCount === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-600">
                  Store agreements, IDs, and other files for this closed deal.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[#234952] focus:outline-none focus:ring-2 focus:ring-[#2A565E]/40 focus:ring-offset-1">
              <Plus className="h-4 w-4" strokeWidth={2.25} />
              Add document
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {isListLoading && (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="flex animate-pulse items-center gap-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="h-12 w-12 rounded-xl bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded bg-slate-200" />
                    <div className="h-3 w-1/2 rounded bg-slate-100" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {showEmpty && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-14 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-700/10 text-teal-800">
                <Upload className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h3 className="text-base font-semibold text-slate-900">No documents yet</h3>
              <p className="mt-1 max-w-sm text-sm text-slate-500">
                Attach PDFs, images, or spreadsheets. They stay linked to this deal for your team.
              </p>
              <button
                type="button"
                onClick={() => setShowUploadModal(true)}
                className="mt-5 inline-flex items-center gap-2 rounded-lg border border-[#2A565E]/25 bg-white px-4 py-2 text-sm font-medium text-[#2A565E] shadow-sm transition hover:bg-teal-50/80">
                <Plus className="h-4 w-4" />
                Upload first document
              </button>
            </div>
          )}

          {hasAttachments && (
            <div className="overflow-hidden rounded-xl border border-slate-200/90">
              <div className="hidden border-b border-slate-100 bg-slate-50/80 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:grid sm:grid-cols-[1fr_140px_100px] sm:gap-4">
                <span>Document</span>
                <span>Uploaded</span>
                <span className="text-right">Action</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {attachments.map((doc) => {
                  const visual = getFileVisual(doc?.name, doc?.mime_type);
                  const Icon = visual.Icon;
                  const sizeLabel = formatFileSize(doc?.size_bytes);
                  const isDownloading = downloadingId === doc.id;

                  return (
                    <li
                      key={doc.id}
                      className="group grid gap-3 bg-white px-4 py-4 transition hover:bg-slate-50/60 sm:grid-cols-[1fr_140px_100px] sm:items-center sm:gap-4">
                      <div className="flex min-w-0 items-start gap-3 sm:col-span-1">
                        <div
                          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ${visual.bg} ${visual.text} ${visual.ring}`}>
                          <Icon className="h-6 w-6" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-slate-900" title={doc?.name}>
                            {doc?.name || '—'}
                          </p>
                          {doc?.description && (
                            <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">
                              {doc.description}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-2 sm:hidden">
                            <MetaChip icon={Calendar} label={formatDate(doc?.created_at)} />
                            {sizeLabel && <MetaChip icon={HardDrive} label={sizeLabel} />}
                            {doc?.uploader?.name && (
                              <MetaChip icon={User} label={doc.uploader.name} />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="hidden flex-col gap-1.5 text-sm text-slate-600 sm:flex">
                        <span className="font-medium text-slate-700">
                          {formatDate(doc?.created_at)}
                        </span>
                        {doc?.uploader?.name && (
                          <span className="truncate text-xs text-slate-500">{doc.uploader.name}</span>
                        )}
                        {sizeLabel && (
                          <span className="text-xs text-slate-400">{sizeLabel}</span>
                        )}
                      </div>

                      <div className="flex sm:justify-end">
                        <button
                          type="button"
                          onClick={() => downloadAttachment(doc.id)}
                          disabled={isDownloading}
                          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-teal-200 hover:bg-teal-50/50 hover:text-teal-900 disabled:opacity-60 sm:w-auto">
                          {isDownloading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          Download
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {showUploadModal && (
        <Modal name="Add document" width={48} onClose={closeModal}>
          <form onSubmit={handleSubmitAttachment} className="px-6 pb-6 pt-2">
            <p className="mb-5 text-sm text-slate-500">
              Give the file a clear name, then upload. It will be saved to this closed deal.
            </p>

            <div className="space-y-5">
              <div>
                <label
                  htmlFor="doc-attachment-name"
                  className="mb-1.5 block text-sm font-medium text-slate-700">
                  Document name <span className="text-red-500">*</span>
                </label>
                <input
                  id="doc-attachment-name"
                  type="text"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  maxLength={255}
                  placeholder="e.g. Signed agreement"
                  disabled={isSubmitting}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/20 disabled:bg-slate-50"
                />
              </div>

              <div>
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  File <span className="text-red-500">*</span>
                </span>
                <input
                  ref={modalFileInputRef}
                  type="file"
                  className="sr-only"
                  onChange={handleModalFileChange}
                  disabled={isUploadingFile || isSubmitting}
                />
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && modalFileInputRef.current?.click()}
                  onClick={() => !isUploadingFile && !isSubmitting && modalFileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${
                    uploadedFileData
                      ? 'border-emerald-200 bg-emerald-50/40'
                      : 'border-slate-200 bg-slate-50/50 hover:border-teal-300 hover:bg-teal-50/30'
                  } ${isUploadingFile || isSubmitting ? 'pointer-events-none opacity-60' : ''}`}>
                  {isUploadingFile ? (
                    <>
                      <Loader2 className="mb-2 h-8 w-8 animate-spin text-teal-700" />
                      <p className="text-sm font-medium text-slate-700">Uploading…</p>
                    </>
                  ) : uploadedFileData ? (
                    <>
                      <CheckCircle2 className="mb-2 h-8 w-8 text-emerald-600" />
                      <p className="text-sm font-medium text-slate-800">
                        {selectedFile?.name || uploadedFileData.name}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Click or drop to replace</p>
                    </>
                  ) : (
                    <>
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200/80">
                        <Upload className="h-5 w-5 text-teal-800" />
                      </div>
                      <p className="text-sm font-medium text-slate-800">
                        Drag & drop or click to browse
                      </p>
                      <p className="mt-1 text-xs text-slate-500">PDF, images, Word, Excel</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-5">
              <button
                type="button"
                onClick={closeModal}
                disabled={isSubmitting || isUploadingFile}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isUploadingFile || !uploadedFileData}
                className="inline-flex min-w-[5.5rem] items-center justify-center gap-2 rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[#234952] disabled:cursor-not-allowed disabled:opacity-50">
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};

function MetaChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100/90 px-2 py-0.5 text-xs text-slate-600">
      <Icon className="h-3 w-3 shrink-0 opacity-70" />
      <span className="truncate">{label}</span>
    </span>
  );
}

export default DealDocumentAttachments;
